'use client'

import { ColumnDef } from '@tanstack/react-table'
import { Ellipsis, Mail } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge'
import { DateDisplay } from '@/components/date-display'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { EmailTemplateItem } from '@/lib/api/emailTemplates'

/**
 * Humanize an email template type enum (e.g. `order_refunded` → `Order refunded`)
 * so the Type badge matches the copy used elsewhere in the editor.
 */
function humanizeTemplateType(type: string | null | undefined): string {
	if (!type) return '—'
	const spaced = type.replace(/_/g, ' ')
	return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

interface GetEmailTemplatesTableColumnsProps {
	onEditTemplate: (templateId: string) => void
	onDeleteTemplate: (templateId: string) => void
}

export function useEmailTemplatesTableColumns({
	onEditTemplate,
	onDeleteTemplate,
}: GetEmailTemplatesTableColumnsProps): ColumnDef<EmailTemplateItem>[] {
	const { t } = useTranslation('emails')

	return React.useMemo(
		() => [
			{
				id: 'select',
				header: ({ table }) => (
					<Checkbox
						checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
						onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
						aria-label={t('table.selectAll')}
						className="translate-y-0.5"
					/>
				),
				cell: ({ row }) => (
					<DataTableCheckbox row={row} />
				),
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
			{
				id: 'name',
				accessorKey: 'name',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title={t('table.columns.name')} />
				),
				cell: ({ row }) => {
					const template = row.original
					return (
						<div className="min-w-0">
							<button
								type="button"
								onClick={() => onEditTemplate(template.id)}
								className="truncate font-medium text-left hover:underline"
							>
								{template.name || t('table.untitled')}
							</button>
							<div className="truncate text-xs text-muted-foreground">
								{template.subject?.['en'] || template.subject?.[Object.keys(template.subject || {})[0]] || t('table.noSubject')}
							</div>
						</div>
					)
				},
				meta: {
					label: t('table.columns.name'),
					placeholder: t('table.searchPlaceholder'),
					variant: 'text',
					icon: () => <Mail />,
				},
				enableColumnFilter: true,
				enableSorting: true,
			},
			{
				id: 'type',
				accessorKey: 'type',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title={t('table.columns.type')} />
				),
				cell: ({ row }) => {
					const template = row.original
					return (
						<Badge variant="secondary" className="capitalize">
							{humanizeTemplateType(template.type)}
						</Badge>
					)
				},
				enableSorting: true,
				meta: { label: t('table.columns.type') },
			},
			{
				id: 'active',
				accessorKey: 'active',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title={t('table.columns.active')} />
				),
				cell: ({ row }) => {
					const template = row.original
					return (
						<Badge
							variant={template.active ? 'green' : 'gray'}
							className="min-w-[72px] justify-center capitalize"
						>
							{template.active
								? t('table.active')
								: t('table.inactive')}
						</Badge>
					)
				},
				enableSorting: true,
				meta: { label: t('table.columns.active') },
			},
			{
				id: 'createdAt',
				accessorKey: 'createdAt',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title={t('table.columns.createdAt')} />
				),
				cell: ({ row }) => (
					<DateDisplay
						value={row.original.createdAt}
						mode="date"
						className="text-sm text-muted-foreground"
						fallback="-"
					/>
				),
				enableSorting: true,
				meta: { label: t('table.columns.createdAt') },
			},
			// Template ID (moved to end, copyable) — matches convention in
			// products, orders, customers, payments tables across the app.
			{
				id: 'id',
				accessorKey: 'id',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title={t('table.columns.id')} />
				),
				cell: ({ row }) => {
					const template = row.original
					const displayValue = `${template.id.substring(0, 8)}...`
					return (
						<CopyableResourceBadge
							name={t('table.columns.id')}
							value={displayValue}
							copyValue={template.id}
						/>
					)
				},
				enableSorting: true,
				enableHiding: true,
				size: 140,
				meta: { label: t('table.columns.id') },
			},
			{
				id: 'actions',
				cell: ({ row }) => {
					const template = row.original
					return (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									aria-label={t('table.openMenu')}
									variant="ghost"
									className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
								>
									<Ellipsis className="h-4 w-4" aria-hidden="true" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-[160px]">
								<DropdownMenuItem onClick={() => onEditTemplate(template.id)}>
									{t('table.editAction')}
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={() => onDeleteTemplate(template.id)}
									className="text-destructive focus:text-destructive"
								>
									{t('table.deleteAction')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)
				},
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
		],
		[t, onEditTemplate, onDeleteTemplate],
	)
}
